// Cross-article highlights view: every highlight across every bookmark,
// newest first, with search and "load more" pagination. Cards reuse the
// reader sidebar's markup/classes (annotation-card, annotation-comment,
// annotation-actions — see annotations.ts's renderSidebar) for visual
// consistency, but edit/delete talk to the API directly here rather than
// sharing that module's state, which is tied to whichever single article is
// currently open.
import { api } from './store.ts';
import { el, formatDate, debounce } from '../../ui/shared/dom.ts';
import type { Highlight } from '../../ui/shared/types.ts';

/**
 * Look up an element that the page is expected to contain.
 *
 * Throws rather than returning null: every id here is in index.html, so a
 * miss means the markup and the script have diverged — which is exactly the
 * failure that should be loud rather than a silent no-op further down.
 */
function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

const PAGE_SIZE = 20;

let highlights: Highlight[] = [];
let query = '';
let offset = 0;
let hasMore = false;
let loading = false;

function renderCard(highlight: Highlight): HTMLElement {
  return el('div', {
    class: 'annotation-card highlight-card',
    dataset: { id: String(highlight.id) },
  }, [
    el('a', { class: 'highlight-source', href: `#/read/${highlight.bookmark_id}` },
      `${highlight.bookmark_title || highlight.bookmark_url} · ${formatDate(highlight.created_at)}` +
      (highlight.page != null ? ` · p. ${highlight.page}` : '')),
    el('blockquote', {
      onclick: () => { window.location.hash = `#/read/${highlight.bookmark_id}`; },
    }, highlight.quote),
    highlight.comment && el('div', { class: 'annotation-comment' }, highlight.comment),
    el('div', { class: 'annotation-actions' }, [
      el('button', { class: 'btn-sm', type: 'button', onclick: () => editComment(highlight) },
        highlight.comment ? 'edit note' : 'add note'),
      el('button', { class: 'btn-sm danger', type: 'button', onclick: () => removeHighlight(highlight) },
        'delete'),
    ]),
  ]);
}

function renderList(): void {
  const list = $('highlights-list');
  if (!highlights.length) {
    list.replaceChildren(el('div', { class: 'msg' },
      query
        ? 'No highlights match your search.'
        : 'Nothing highlighted yet — select text in any article to start.'));
  } else {
    list.replaceChildren(...highlights.map(renderCard));
  }
  $('highlights-load-more').hidden = !hasMore;
}

async function fetchPage(reset: boolean): Promise<void> {
  if (loading) return;
  loading = true;
  try {
    const nextOffset = reset ? 0 : offset;
    const page = await api.getHighlights({ q: query || undefined, limit: PAGE_SIZE, offset: nextOffset });
    highlights = reset ? page : [...highlights, ...page];
    offset = nextOffset + page.length;
    // The list doesn't expose X-Total-Count, so a full page is treated as
    // "there might be more" — worst case, one extra click comes back empty.
    hasMore = page.length === PAGE_SIZE;
    renderList();
  } catch (err) {
    $('highlights-list').replaceChildren(el('div', { class: 'reader-error' }, (err as Error).message));
  } finally {
    loading = false;
  }
}

async function editComment(highlight: Highlight): Promise<void> {
  const next = window.prompt('Comment', highlight.comment || '');
  if (next === null) return;
  try {
    const updated = await api.updateAnnotation(highlight.id, { comment: next.trim() });
    highlights = highlights.map((h) => (h.id === highlight.id ? { ...h, ...updated } : h));
    renderList();
  } catch (err) {
    alert((err as Error).message);
  }
}

async function removeHighlight(highlight: Highlight): Promise<void> {
  try {
    await api.deleteAnnotation(highlight.id);
    highlights = highlights.filter((h) => h.id !== highlight.id);
    renderList();
  } catch (err) {
    alert((err as Error).message);
  }
}

const search = debounce((value: string) => {
  query = value.trim();
  fetchPage(true);
}, 300);

export async function openHighlightsView(): Promise<void> {
  $<HTMLInputElement>('highlights-search').value = '';
  query = '';
  offset = 0;
  hasMore = false;
  highlights = [];
  $('highlights-list').replaceChildren(el('div', { class: 'msg' }, 'loading...'));
  await fetchPage(true);
}

export function closeHighlightsView(): void {
  highlights = [];
}

$('highlights-search').addEventListener('input', (e: Event) => {
  search((e.target as HTMLInputElement).value);
});

$('highlights-load-more').addEventListener('click', () => fetchPage(false));
$('highlights-back').addEventListener('click', () => { window.location.hash = ''; });
